import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Linkedin } from "lucide-react";
import { useTranslation } from "react-i18next";
import Logo from "./Logo";
import { useDemo } from "../context/DemoContext";
import { SITE } from "../lib/seo";

const modules = [
  ["Membership", "membership"], ["Conferences", "conference"], ["Submissions & Reviews", "submissions-reviews"],
  ["Mobile App", "mobile-app"], ["Continuing Education", "continuing-education"], ["Virtual / Hybrid", "virtual-hybrid"],
  ["e-Poster Gallery", "eposter-gallery"], ["Job Board", "job-board"], ["Year-Round Surveys", "year-round-surveys"],
  ["Task Management", "task-management"], ["Reporting", "reporting"], ["Integrations & SSO", "api-integrations"],
];

const offices = [
  { city: "Brentwood, TN", country: "USA", flag: "https://flagcdn.com/w40/us.png" },
  { city: "Toronto, ON", country: "Canada", flag: "https://flagcdn.com/w40/ca.png" },
  { city: "Coimbatore, TN", country: "India", flag: "https://flagcdn.com/w40/in.png" },
];

export default function Footer() {
  const { openDemo } = useDemo();
  const { t } = useTranslation();
  return (
    <footer data-testid="site-footer" className="bg-foreground text-white">
      <div className="h-1 joyn-gradient-bar" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24 md:py-28 border-b border-white/10">
        <p className="uppercase tracking-[0.25em] text-xs font-bold text-joyn-lime mb-6">{t("footer.ctaEyebrow")}</p>
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-10">
          <h2 className="font-display font-extrabold text-4xl md:text-6xl tracking-tight leading-[1.02] max-w-3xl">{t("footer.ctaTitle")}</h2>
          <button data-testid="footer-schedule-demo-btn" onClick={openDemo}
            className="group inline-flex items-center gap-3 bg-white text-foreground font-semibold text-lg px-8 py-4 rounded-full hover:-translate-y-1 transition-transform shrink-0">
            {t("nav.demo")}
            <ArrowUpRight className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" size={20} />
          </button>
        </div>
      </div>

      {/* Offices */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-12 border-b border-white/10">
        <p className="uppercase tracking-[0.2em] text-xs font-bold text-white/40 mb-6">{t("footer.offices")}</p>
        <div className="grid sm:grid-cols-3 gap-5" data-testid="footer-offices">
          {offices.map((o) => (
            <div key={o.city} className="flex items-center gap-4 bg-white/5 rounded-2xl p-5 border border-white/10">
              <img src={o.flag} alt={o.country} className="h-7 w-10 object-cover rounded-[3px] shrink-0" />
              <div>
                <p className="font-display font-bold leading-tight">{o.city}</p>
                <p className="text-sm text-white/60 flex items-center gap-1"><MapPin size={12} /> {o.country}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2 md:col-span-1">
          <Logo dark height={52} />
          <p className="mt-5 text-sm text-white/60 leading-relaxed max-w-xs">{t("footer.desc")}</p>
          <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin" aria-label={t("social.cta")}
            className="mt-6 inline-flex items-center gap-2.5 text-sm font-semibold text-white/70 hover:text-white transition-colors">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 border border-white/15"><Linkedin size={18} /></span>
            LinkedIn
          </a>
        </div>
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-white/40 mb-5">{t("footer.platform")}</p>
          <ul className="space-y-2.5">
            {modules.slice(0, 6).map(([label, slug]) => (
              <li key={slug}><Link to={`/products/${slug}`} data-testid={`footer-product-${slug}`} className="text-sm text-white/70 hover:text-white transition-colors">{label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-white/40 mb-5">{t("footer.moreModules")}</p>
          <ul className="space-y-2.5">
            {modules.slice(6).map(([label, slug]) => (
              <li key={slug}><Link to={`/products/${slug}`} className="text-sm text-white/70 hover:text-white transition-colors">{label}</Link></li>
            ))}
          </ul>
        </div>
        <div>
          <p className="uppercase tracking-[0.2em] text-xs font-bold text-white/40 mb-5">{t("footer.company")}</p>
          <ul className="space-y-2.5">
            <li><Link to="/ai" data-testid="footer-joyn-ai" className="text-sm text-white/70 hover:text-white transition-colors">{t("footer.links.ai")}</Link></li>
            <li><Link to="/who-we-serve" className="text-sm text-white/70 hover:text-white transition-colors">{t("footer.links.who")}</Link></li>
            <li><Link to="/support" className="text-sm text-white/70 hover:text-white transition-colors">{t("footer.links.support")}</Link></li>
            <li><Link to="/about" className="text-sm text-white/70 hover:text-white transition-colors">{t("footer.links.about")}</Link></li>
            <li><Link to="/contact" className="text-sm text-white/70 hover:text-white transition-colors">{t("footer.links.contact")}</Link></li>
            <li><a href="mailto:sales@joynams.com" className="text-sm text-white/70 hover:text-white transition-colors">sales@joynams.com</a></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-xs text-white/40">
            © {new Date().getFullYear()} joyn Association Management Software. {t("footer.bottom")}
            {" · "}
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-white transition-colors underline-offset-2 hover:underline">Privacy Policy</Link>
          </p>
          <p className="text-xs text-white/40">{t("footer.tagline")}</p>
        </div>
      </div>
    </footer>
  );
}
